if (!customElements.get('judgeme-reviews')) {
  customElements.define(
    'judgeme-reviews',
    class JudgeMeReviews extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.shopDomain = this.dataset.shopDomain;
        this.apiToken = this.dataset.apiToken;
        this.productId = this.dataset.productId;
        this.productHandle = this.dataset.productHandle;
        this.productUrl = this.dataset.productUrl;
        this.perPage = Number(this.dataset.perPage || 2);
        this.customerName = this.dataset.customerName || '';
        this.customerEmail = this.dataset.customerEmail || '';
        this.visibleCount = this.perPage;
        this.reviews = [];
        this.histogram = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        this.average = Number(this.dataset.average || 0);
        this.totalCount = Number(this.dataset.count || 0);
        this.starIcon = this.querySelector('.pdp-rating__star')?.innerHTML || '';

        this.list = this.querySelector('[data-list]');
        this.status = this.querySelector('[data-status]');
        this.showing = this.querySelector('[data-showing]');
        this.showAllBtn = this.querySelector('[data-show-all]');
        this.searchInput = this.querySelector('[data-search]');
        this.sortSelect = this.querySelector('[data-sort]');
        this.template = this.querySelector('[data-review-template]');
        this.dialog = this.querySelector('[data-write-dialog]');
        this.writeForm = this.querySelector('[data-write-form]');
        this.starPicker = this.querySelector('[data-star-picker]');

        this.facet = this.querySelector('.judgeme-reviews__facet');
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.syncFacetOpen();
        this.desktopQuery.addEventListener('change', () => this.syncFacetOpen());
        this.classList.add('is-ready');

        this.querySelector('[data-open-write]')?.addEventListener('click', () => this.openWrite());
        this.querySelector('[data-close-write]')?.addEventListener('click', () => this.closeWrite());
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.closeWrite();
        });
        this.searchInput?.addEventListener('input', () => {
          this.visibleCount = this.perPage;
          this.render();
        });
        this.sortSelect?.addEventListener('change', () => this.render());
        this.querySelectorAll('[data-star-filter]').forEach((input) => {
          input.addEventListener('change', () => {
            this.visibleCount = this.perPage;
            this.render();
          });
        });
        this.showAllBtn?.addEventListener('click', () => {
          this.visibleCount = this.filtered().length;
          this.render();
        });
        this.writeForm?.addEventListener('submit', (event) => this.submitReview(event));
        this.list?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-upvote]');
          if (!button || !this.list.contains(button)) return;
          event.preventDefault();
          const review = this.reviews.find((item) => String(item.id) === String(button.dataset.reviewId));
          if (!review) return;
          this.toggleUpvote(review, button, button.querySelector('[data-upvote-count]'));
        });
        this.starPicker?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-star-value]');
          if (!button) return;
          this.setWriteRating(Number(button.dataset.starValue));
        });

        this.setWriteRating(5);
        this.setStatus('Loading reviews...');
        this.loadReviews();
      }

      syncFacetOpen() {
        if (!this.facet) return;
        this.facet.open = this.desktopQuery.matches;
      }

      widgetUrl(page, extra = {}) {
        const params = new URLSearchParams({
          api_token: this.apiToken,
          shop_domain: this.shopDomain,
          platform: 'shopify',
          external_id: this.productId,
          handle: this.productHandle,
          page: String(page),
          per_page: '20',
        });
        Object.entries(extra).forEach(([key, value]) => params.set(key, value));
        return `https://judge.me/api/v1/widgets/product_review?${params.toString()}`;
      }

      async loadReviews() {
        try {
          const first = await this.fetchPage(1);
          this.applyPayload(first, true);
          const total = this.totalCount || this.reviews.length;
          const pages = Math.min(10, Math.ceil(total / 20) || 1);
          for (let page = 2; page <= pages; page += 1) {
            if (this.reviews.length >= total) break;
            const next = await this.fetchPage(page);
            this.applyPayload(next, false);
          }
          if (!this.totalCount) this.totalCount = this.reviews.length;
          const fromReviews = this.countHistogram(this.reviews);
          this.histogram =
            this.histogramTotal(fromReviews) >= this.histogramTotal(this.histogram) ? fromReviews : this.histogram;
          this.updateSummary();
          this.render();
        } catch (error) {
          this.setStatus('Reviews could not be loaded right now.');
        }
      }

      async fetchPage(page) {
        const request = async (extra) => {
          const response = await fetch(this.widgetUrl(page, extra), { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Judge.me request failed');
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) return response.json();
          return { widget: await response.text() };
        };

        try {
          return await request({ json_request: 'true' });
        } catch (error) {
          return request({});
        }
      }

      applyPayload(payload, replace) {
        const parsed = this.normalizePayload(payload);
        if (replace) {
          this.reviews = parsed.reviews;
          if (parsed.average) this.average = parsed.average;
          if (parsed.count) this.totalCount = parsed.count;
          this.histogram = this.mergeHistogram(parsed.histogram, this.countHistogram(parsed.reviews));
        } else {
          const seen = new Set(this.reviews.map((review) => String(review.id)));
          parsed.reviews.forEach((review) => {
            if (!seen.has(String(review.id))) this.reviews.push(review);
          });
        }
      }

      normalizePayload(payload) {
        if (!payload) return { reviews: [] };
        if (typeof payload === 'string') return this.parseWidgetHtml(payload);
        if (payload.widget) {
          const fromHtml = this.parseWidgetHtml(payload.widget);
          const fromJson = this.reviewsFromJson(payload);
          return {
            average: Number(payload.average_rating || payload.averageRating || fromHtml.average || 0),
            count: Number(
              payload.number_of_reviews || payload.reviews_count || fromHtml.count || fromJson.length || 0
            ),
            histogram: this.histogramFrom(payload) || fromHtml.histogram,
            reviews: fromJson.length ? fromJson : fromHtml.reviews,
          };
        }
        const reviews = this.reviewsFromJson(payload);
        return {
          average: Number(payload.average_rating || payload.averageRating || this.average || 0),
          count: Number(payload.number_of_reviews || payload.reviews_count || reviews.length || 0),
          histogram: this.histogramFrom(payload),
          reviews,
        };
      }

      reviewsFromJson(payload) {
        const list = payload.reviews || payload.data || [];
        if (!Array.isArray(list)) return [];
        return list.map((item) => this.normalizeReview(item)).filter(Boolean);
      }

      histogramFrom(payload) {
        const candidates = [
          payload.number,
          payload.rating_count,
          payload.histogram,
          payload.ratingCount,
          payload.histogram?.all,
          payload.product?.histogram,
          payload.product?.rating_count,
          payload.meta?.histogram,
        ];
        const source = candidates.find((item) => item && typeof item === 'object' && !Array.isArray(item));
        if (!source) return null;
        const histogram = {
          1: Number(source['1'] || source[1] || source.one || 0),
          2: Number(source['2'] || source[2] || source.two || 0),
          3: Number(source['3'] || source[3] || source.three || 0),
          4: Number(source['4'] || source[4] || source.four || 0),
          5: Number(source['5'] || source[5] || source.five || 0),
        };
        return this.hasHistogramValues(histogram) ? histogram : null;
      }

      countHistogram(reviews) {
        const histogram = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        (reviews || []).forEach((review) => {
          const rating = Math.round(Number(review.rating) || 0);
          if (histogram[rating] != null) histogram[rating] += 1;
        });
        return histogram;
      }

      hasHistogramValues(histogram) {
        return Boolean(histogram && Object.values(histogram).some((count) => Number(count) > 0));
      }

      mergeHistogram(primary, fallback) {
        if (this.hasHistogramValues(primary)) return primary;
        if (this.hasHistogramValues(fallback)) return fallback;
        return primary || fallback || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      }

      histogramTotal(histogram) {
        return Object.values(histogram || {}).reduce((sum, count) => sum + Number(count || 0), 0);
      }

      parseWidgetHtml(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const widget = doc.querySelector('.jdgm-rev-widg, .jdgm-widget, [data-average-rating]');
        const histogram = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        doc.querySelectorAll('.jdgm-histogram__row, .jdgm-histogram [data-rating], .jdgm-histogram [data-frequency]').forEach((row) => {
          const rating = Number(
            row.getAttribute('data-rating') || row.getAttribute('data-degree') || row.getAttribute('data-score')
          );
          const frequencyText = row.querySelector('.jdgm-histogram__frequency')?.textContent || '';
          const frequency = Number(
            row.getAttribute('data-frequency') ||
              row.getAttribute('data-count') ||
              frequencyText.replace(/[^\d]/g, '') ||
              0
          );
          if (rating >= 1 && rating <= 5) histogram[rating] = frequency;
        });
        const reviewNodes = doc.querySelectorAll('.jdgm-rev');
        const nodes = reviewNodes.length ? reviewNodes : doc.querySelectorAll('[data-review-id]');
        const reviews = [...nodes].map((node) => {
          const pictures = [...node.querySelectorAll('.jdgm-rev__pic-img, .jdgm-rev__pics img')].map((img) => img.src);
          return {
            id: node.getAttribute('data-review-id') || node.getAttribute('data-review-uuid') || node.getAttribute('data-id'),
            rating: Number(node.getAttribute('data-rating') || node.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || 0),
            author: node.querySelector('.jdgm-rev__author')?.textContent.trim() || '',
            title: node.querySelector('.jdgm-rev__title')?.textContent.trim() || '',
            body: node.querySelector('.jdgm-rev__body')?.textContent.trim() || '',
            created_at: node.querySelector('.jdgm-rev__timestamp')?.getAttribute('data-content') || '',
            time_ago: node.querySelector('.jdgm-rev__timestamp')?.textContent.trim() || '',
            verified: Boolean(node.querySelector('.jdgm-rev__buyer-badge')),
            pictures,
            votes_up: Number(node.querySelector('.jdgm-rev__helpful-count')?.textContent || 0),
          };
        });
        return {
          average: Number(widget?.getAttribute('data-average-rating') || 0),
          count: Number(widget?.getAttribute('data-number-of-reviews') || reviews.length || 0),
          histogram,
          reviews,
        };
      }

      normalizeReview(item) {
        if (!item) return null;
        const reviewer = item.reviewer || {};
        return {
          id: item.uuid || item.review_uuid || item.token || item.id,
          rating: Number(item.rating || 0),
          author: item.reviewer_name || reviewer.name || item.name || '',
          title: item.title || '',
          body: item.body || item.content || '',
          created_at: item.created_at || item.updated_at || '',
          time_ago: item.time_ago || '',
          verified: item.verified === 'buyer' || item.verified === true,
          pictures: (item.pictures || [])
            .map((picture) => picture?.urls?.compact || picture?.urls?.small || picture?.urls?.huge || picture)
            .filter((src) => typeof src === 'string'),
          votes_up: Number(item.votes_up || item.thumbs_up || item.helpful_count || 0),
        };
      }

      selectedStars() {
        return [...this.querySelectorAll('[data-star-filter]:checked')].map((input) => Number(input.value));
      }

      filtered() {
        const query = (this.searchInput?.value || '').trim().toLowerCase();
        const stars = this.selectedStars();
        const sort = this.sortSelect?.value || 'highest';
        const reviews = this.reviews.filter((review) => {
          if (stars.length && !stars.includes(Number(review.rating))) return false;
          if (!query) return true;
          const haystack = `${review.title} ${review.body} ${review.author}`.toLowerCase();
          return haystack.includes(query);
        });

        const timestamp = (review) => Date.parse(review.created_at) || 0;
        reviews.sort((a, b) => {
          if (sort === 'lowest') return a.rating - b.rating || timestamp(b) - timestamp(a);
          if (sort === 'newest') return timestamp(b) - timestamp(a);
          if (sort === 'oldest') return timestamp(a) - timestamp(b);
          return b.rating - a.rating || timestamp(b) - timestamp(a);
        });
        return reviews;
      }

      render() {
        if (!this.list || !this.template) return;
        const reviews = this.filtered();
        const visible = reviews.slice(0, this.visibleCount);
        const isEmpty = !this.reviews.length;
        this.list.replaceChildren();
        this.classList.toggle('is-empty', isEmpty);

        if (isEmpty) {
          this.setStatus('');
        } else if (!reviews.length) {
          this.setStatus('No reviews match your filters.');
        } else {
          this.setStatus('');
          visible.forEach((review) => this.list.appendChild(this.renderCard(review)));
        }

        if (this.showing) this.showing.textContent = `Showing ${visible.length} of ${reviews.length} results`;
        if (this.showAllBtn) this.showAllBtn.hidden = reviews.length <= visible.length;
        this.querySelectorAll('[data-star-count]').forEach((el) => {
          const rating = Number(el.dataset.starCount);
          el.textContent = this.histogram[rating] ?? this.histogram[String(rating)] ?? 0;
        });
      }

      renderCard(review) {
        const fragment = this.template.content.cloneNode(true);
        const card = fragment.querySelector('.judgeme-review');
        const stars = card.querySelector('[data-stars]');
        const time = card.querySelector('[data-time]');
        const title = card.querySelector('[data-title]');
        const body = card.querySelector('[data-body]');
        const photos = card.querySelector('[data-photos]');
        const upvote = card.querySelector('[data-upvote]');
        const upvoteCount = card.querySelector('[data-upvote-count]');
        const commentToggle = card.querySelector('[data-toggle-comment]');
        const commentForm = card.querySelector('[data-comment-form]');

        stars.innerHTML = this.starRow(review.rating, review.id);
        time.textContent = review.time_ago || this.relativeTime(review.created_at);
        title.textContent = review.title || review.author || 'Review';
        body.textContent = review.body || '';

        if (review.pictures?.length) {
          photos.hidden = false;
          review.pictures.forEach((src) => {
            const img = document.createElement('img');
            img.src = src;
            img.alt = '';
            img.width = 64;
            img.height = 64;
            photos.appendChild(img);
          });
        }

        if (upvote && review.id != null) upvote.dataset.reviewId = String(review.id);

        const likedCount = Number(this.readStore(this.reviewKey(review)) || 0);
        const votes = Math.max(Number(review.votes_up || 0), likedCount);
        if (likedCount) {
          review.votes_up = votes;
          this.markLiked(upvote, upvoteCount, votes || 1);
        } else if (votes) {
          upvoteCount.hidden = false;
          upvoteCount.textContent = String(votes);
        }

        commentToggle?.addEventListener('click', () => {
          commentForm.hidden = !commentForm.hidden;
        });
        commentForm?.addEventListener('submit', (event) => this.submitComment(event, review, commentForm));
        if (this.customerName) commentForm.querySelector('[name="name"]').value = this.customerName;
        if (this.customerEmail) commentForm.querySelector('[name="email"]').value = this.customerEmail;
        return card;
      }

      starRow(rating, key = 'x') {
        return Array.from({ length: 5 }, (_, index) => {
          const on = index < Number(rating);
          const icon = this.starIcon.replace(/paint0_linear_1437_4321/g, `jdgm-star-${key}-${index}`);
          return `<span class="${on ? '' : 'is-empty'}">${icon}</span>`;
        }).join('');
      }

      relativeTime(value) {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        const diff = Math.round((Date.now() - date.getTime()) / 1000);
        if (diff < 60) return 'Just now';
        if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
        if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      }

      updateSummary() {
        const average = `${Number(this.average || 0).toFixed(1)}/5`;
        const count = `${this.totalCount || this.reviews.length} Reviews`;
        const averageEl = this.querySelector('[data-average-label]');
        const countEl = this.querySelector('[data-count-label]');
        if (averageEl) averageEl.textContent = average;
        if (countEl) countEl.textContent = count;

        document.querySelectorAll('[data-pdp-reviews-average]').forEach((el) => {
          el.textContent = average;
        });
        document.querySelectorAll('[data-pdp-reviews-count]').forEach((el) => {
          el.textContent = count;
        });
      }

      setStatus(message) {
        if (!this.status) return;
        this.status.hidden = !message;
        this.status.textContent = message || '';
      }

      openWrite() {
        this.querySelector('[data-form-error]').hidden = true;
        this.querySelector('[data-form-success]').hidden = true;
        this.dialog?.showModal();
      }

      closeWrite() {
        this.dialog?.close();
      }

      setWriteRating(value) {
        const rating = Number(value) || 5;
        this.writeForm.querySelector('[name="rating"]').value = String(rating);
        this.starPicker.querySelectorAll('[data-star-value]').forEach((button) => {
          button.classList.toggle('is-on', Number(button.dataset.starValue) <= rating);
        });
      }

      async submitReview(event) {
        event.preventDefault();
        const form = event.currentTarget;
        const error = form.querySelector('[data-form-error]');
        const success = form.querySelector('[data-form-success]');
        error.hidden = true;
        success.hidden = true;
        const data = Object.fromEntries(new FormData(form).entries());
        try {
          const response = await fetch('https://judge.me/api/v1/reviews', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              shop_domain: this.shopDomain,
              platform: 'shopify',
              id: Number(this.productId),
              url: this.productUrl,
              name: data.name,
              email: data.email,
              rating: Number(data.rating),
              title: data.title,
              body: data.body,
            }),
          });
          if (!response.ok) throw new Error('Could not submit review');
          success.hidden = false;
          success.textContent = 'Thank you. Your review has been submitted and will appear after approval.';
          form.reset();
          this.setWriteRating(5);
        } catch (err) {
          error.hidden = false;
          error.textContent = 'Could not submit your review. Please try again.';
        }
      }

      reviewKey(review) {
        const id = review?.id;
        if (id == null || String(id).trim() === '' || String(id) === 'undefined') return '';
        return `jdgm-up-${id}`;
      }

      readStore(key) {
        if (!key) return '';
        try {
          return window.localStorage.getItem(key) || '';
        } catch (error) {
          return '';
        }
      }

      writeStore(key, value) {
        if (!key) return;
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {
          // Ignore private-mode storage failures.
        }
      }

      clearStore(key) {
        if (!key) return;
        try {
          window.localStorage.removeItem(key);
        } catch (error) {
          // Ignore private-mode storage failures.
        }
      }

      markLiked(button, countEl, count) {
        if (!button) return;
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
        if (countEl && count) {
          countEl.hidden = false;
          countEl.textContent = String(count);
        }
      }

      markUnliked(button, countEl, count) {
        if (!button) return;
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
        if (!countEl) return;
        countEl.hidden = !count;
        countEl.textContent = count ? String(count) : '';
      }

      voteBody() {
        return new URLSearchParams({
          shop_domain: this.shopDomain || '',
          platform: 'shopify',
          api_token: this.apiToken || '',
        });
      }

      async sendVote(reviewId, liked) {
        if (!reviewId) return;
        const url = `https://judge.me/reviews/${encodeURIComponent(reviewId)}/upvotes`;
        const body = this.voteBody();
        if (!liked) body.set('_method', 'delete');
        await fetch(url, { method: 'POST', body });
      }

      async toggleUpvote(review, button, countEl) {
        if (!button || button.dataset.busy) return;
        button.dataset.busy = '1';

        const key = this.reviewKey(review);
        const liked = Boolean(this.readStore(key));

        try {
          if (liked) {
            const current = Math.max(Number(review.votes_up || 0), Number(this.readStore(key) || 0));
            const next = Math.max(current - 1, 0);
            review.votes_up = next;
            this.markUnliked(button, countEl, next);
            this.clearStore(key);
            await this.sendVote(review.id, false);
            return;
          }

          const next = Number(review.votes_up || 0) + 1;
          review.votes_up = next;
          this.markLiked(button, countEl, next);
          this.writeStore(key, String(next));
          await this.sendVote(review.id, true);
        } catch (error) {
          // Keep the local helpful state if Judge.me blocks the vote request.
        } finally {
          delete button.dataset.busy;
        }
      }

      async submitComment(event, review, form) {
        event.preventDefault();
        const error = form.querySelector('[data-comment-error]');
        error.hidden = true;
        const data = Object.fromEntries(new FormData(form).entries());
        try {
          const response = await fetch('https://judge.me/reviews/replies', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              shop_domain: this.shopDomain,
              api_token: this.apiToken,
              review_id: review.id,
              name: data.name,
              email: data.email,
              body: data.body,
            }),
          });
          if (!response.ok) throw new Error('Could not submit comment');
          form.reset();
          form.hidden = true;
          error.hidden = false;
          error.style.color = 'var(--color-green-600)';
          error.textContent = 'Comment submitted.';
        } catch (err) {
          error.hidden = false;
          error.style.color = 'var(--color-error-500)';
          error.textContent = 'Could not post this comment right now.';
        }
      }
    }
  );
}
